<template>
  <v-container class="specific-donation-overview standard-content-container">
    <DynamicNavDrawer
      :drawerOpen="readMoreDrawerOpen"
      @drawerClosed="drawerClosed"
      :title="'Key considerations for inheritance choices'"
      :description="'Considerations when creating your will'"
    >
      <template v-slot:content>
        <Accordion
          :accordionData="inheritanceChoicesData"
          :html="true"
          :isNavDrawer="true"
        />
      </template>
    </DynamicNavDrawer>
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link :to="'Overview'" class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>Specific Bequests</h1>
            <p class="specific-donations-description">
              A bequest is a gift of money, property, or specific items that
              someone leaves to another person or organization in their will.
              It's essentially what you decide to give to someone after you pass
              away, such as leaving a certain amount of money to a friend or
              donating a piece of jewelry to a charity.
            </p>
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <AdditionalInfoComponent
                :heading="'Key considerations for inheritance choices'"
                :source="inheritanceChoicesVideo"
                @drawerOpen="drawerOpen()"
              />
            </template>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="7" class="d-flex flex-column donations-container">
        <div class="left-section-container">
          <h2 class="bequest-title">
            Do you want to bequeath an asset or money to someone?
          </h2>
          <ul class="donations-type-list">
            <li>
              Tell us about the specific assets or amounts of money you wish to
              leave to specific people or charities
            </li>
            <li>
              If you have no specific bequests to make you are able to select
              this as an option
            </li>
          </ul>

          <div class="donation-type">
            <BequeathTile
              :title="'Bequeath an asset'"
              :info="'Tell us about the specific assets you wish to  leave to specific people or charities'"
              :buttonText="'Bequeath'"
              @bequeath="bequeath('asset')"
              :bequeathments="categorizedDonations?.assetDonations"
              @editDonation="editDonation"
              @deleteCheckDonation="deleteCheckDonation"
            />
          </div>

          <div class="donation-type">
            <BequeathTile
              :title="'Bequeath money'"
              :info="'Tell us about the specific amounts of money you wish to  leave to specific people or charities'"
              :buttonText="'Bequeath'"
              @bequeath="bequeath('money')"
              :bequeathments="categorizedDonations?.moneyDonations"
              @editDonation="editDonation"
              @deleteCheckDonation="deleteCheckDonation"
            />
          </div>
          <div class="mt-8">
            <Checkbox
              v-if="!hasDonations"
              v-model="specificDonationsOptOut"
              :label="`I don’t want to make any specific bequests`"
              color="primary"
              :class="'opt-out-checkbox'"
            />
            <div class="button-container justify-start">
              <GradientButton
                :title="'Next'"
                :small="true"
                :callout="'right'"
                :loading="buttonLoading"
                :disabled="!hasDonations && !specificDonationsOptOut"
                @gradientButtonClicked="saveAndContinue"
              />
            </div>
          </div>
        </div>
      </v-col>
      <template v-if="$vuetify.breakpoint.smAndDown">
        <AdditionalInfoComponent
          :heading="'Key considerations for inheritance choices'"
          :source="inheritanceChoicesVideo"
          @drawerOpen="drawerOpen()"
        />
      </template>
    </v-row>
    <v-dialog
      v-model="deleteDialog"
      content-class="crud-dialog"
      persistent
      max-width="400"
    >
      <DeleteDialog
        :content="deleteDialogContent"
        :button-loading="deleteButtonLoading"
        @delete="deleteDonation"
        @dialogClosed="deleteDialog = false"
      />
    </v-dialog>
    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import { DELETE_SPECIFIC_DONATION_MUTATION } from '../../constants/graphql/specific-donations';
  import {
    TESTATOR_QUERY,
    UPDATE_TESTATOR_MUTATION,
  } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import Accordion from '../common/Accordion.vue';
  import DynamicNavDrawer from '../guest/DynamicNavDrawer.vue';
  import BequeathTile from './BequeathTile.vue';
  import AdditionalInfoComponent from '../people/AdditionalInfoComponent.vue';
  export default {
    name: 'SpecificDonationsOverview',
    components: {
      DeleteDialog,
      ErrorSnackbar,
      Accordion,
      DynamicNavDrawer,
      BequeathTile,
      AdditionalInfoComponent,
    },
    mixins: [formHelperMixin],
    data() {
      return {
        categorizedDonations: {},
        deleteDialog: false,
        donationToDelete: {},
        testator: {},
        specificDonationsOptOut: false,
        deleteButtonLoading: false,
        loading: true,
        beneficiaryDisplayNameRegex: new RegExp(/\b(?:trust|charity)\s*$/, 'i'),
        inheritanceChoicesVideo: null,
        inheritanceChoicesData: [],
        readMoreDrawerOpen: false,
      };
    },
    assetPropertyNames: {
      Property: 'properties',
      Vehicle: 'vehicles',
      OtherAsset: 'otherAssets',
      FinancialAsset: 'financialAssets',
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        result(response) {
          if (response.data) {
            const viewDonations = this.viewSpecificDonations(
              response.data.testator.specificDonations
            );

            this.categorizedDonations = this.categorizeDonations(viewDonations);
            this.specificDonationsOptOut = Boolean(
              response.data.testator.specificDonationsOptOut
            );
            this.loading = false;
          }
        },
      },
    },
    watch: {
      'testator.specificDonationsOptOut': {
        immediate: true,
        handler(newVal) {
          this.specificDonationsOptOut = Boolean(newVal);
        },
      },
    },
    computed: {
      donationsView() {
        return this.$route.name;
      },
      backRouteName() {
        if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          return 'RemainderOfEstateAlternative';
        } else {
          return 'OverviewPage';
        }
      },
      backRouteHash() {
        if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          return '';
        } else {
          return '#specific-donations';
        }
      },
      assetDonationPath() {
        if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          return '/remainder-of-estate-specific-donations-assets';
        } else {
          return '/specific-donations-assets';
        }
      },
      moneyDonationPath() {
        if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          return '/remainder-of-estate-specific-donations-money';
        } else {
          return '/specific-donations-money';
        }
      },
      deleteDialogContent() {
        return {
          title: 'Remove bequest',
          text: 'Are you sure you want to remove this bequest?',
          buttonText: 'Yes, Remove Bequest',
        };
      },
      hasDonations() {
        return (
          this.categorizedDonations?.moneyDonations?.length ||
          this.categorizedDonations?.assetDonations?.length
        );
      },
      isGBWill() {
        return this.testator.willCountry == 'GB';
      },
      currencySymbol() {
        return this.isGBWill ? '£' : 'R';
      },
      isMobile() {
        return this.$isMobileDevice();
      },
    },
    created() {},
    methods: {
      viewSpecificDonations(specificDonations) {
        if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          return specificDonations.filter(
            donation => donation.donationOrigin !== 'Bequest'
          );
        } else {
          return specificDonations.filter(
            donation => donation.donationOrigin === 'Bequest'
          );
        }
      },
      categorizeDonations(specificDonations) {
        var donations = {};
        donations.assetDonations = [];
        donations.moneyDonations = [];

        for (const donation of specificDonations) {
          switch (donation.donationType) {
            case 'ASSET':
              donations.assetDonations.push(donation);
              break;
            case 'MONEY':
              donations.moneyDonations.push(donation);
              break;
          }
        }
        return donations;
      },
      beneficiaryText(donation) {
        if (donation.donationType === 'MONEY') {
          var text = '';
          donation.beneficiaries.forEach((beneficiary, index) => {
            if (index == 0) {
              text += beneficiary.beneficiaryDisplayName;
            } else if (donation.beneficiaries.length > index + 1) {
              text += ', ' + beneficiary.beneficiaryDisplayName;
            } else if (donation.beneficiaries.length == index + 1) {
              text += ' and ' + beneficiary.beneficiaryDisplayName;
            }
          });
          return text;
        } else {
          var text = '';
          donation.beneficiaries.forEach((beneficiary, index) => {
            if (index == 0) {
              text += this.textForBeneficiaryPercentage(beneficiary);
            } else if (donation.beneficiaries.length > index + 1) {
              text += ', ' + this.textForBeneficiaryPercentage(beneficiary);
            } else if (donation.beneficiaries.length == index + 1) {
              text += ' and ' + this.textForBeneficiaryPercentage(beneficiary);
            }
          });
          return text;
        }
      },
      textForBeneficiaryPercentage(beneficiary) {
        switch (beneficiary.beneficiaryType) {
          case 'Person':
            return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName}`;
          case 'Charity':
            if (
              this.beneficiaryDisplayNameRegex.test(
                beneficiary.beneficiaryDisplayName
              )
            ) {
              return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName}`;
            } else {
              return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName} charity`;
            }
          case 'Trust':
            if (
              this.beneficiaryDisplayNameRegex.test(
                beneficiary.beneficiaryDisplayName
              )
            ) {
              return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName}`;
            } else {
              return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName} trust`;
            }
          default:
            return `${beneficiary.percentage}% to ${beneficiary.beneficiaryDisplayName}`;
        }
      },
      updateTestator() {
        this.buttonLoading = true;
        const testatorInputDetails = {
          specificDonationsOptOut: this.specificDonationsOptOut,
        };
        const id = this.testatorId;

        return this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      editDonation(donation, editLocation) {
        let name = '';
        if (this.donationsView === 'SpecificDonationsOverview') {
          if (donation.donationType === 'ASSET') {
            name = 'AssetDonation';
          } else {
            name = 'MoneyDonation';
          }
        } else if (
          this.donationsView === 'SpecificDonationsRemainderOfEstateOverview'
        ) {
          if (donation.donationType === 'ASSET') {
            name = 'RemainderOfEstateAssetDonation';
          } else {
            name = 'RemainderOfEstateMoneyDonation';
          }
        } else {
          name = editLocation;
        }

        this.$router.push({
          name: name,
          params: { specificDonation: donation },
        });
      },
      saveAndContinue() {
        if (this.hasDonations) {
          this.redirectToNext();
        } else {
          this.updateTestator().then(_result => {
            this.redirectToNext();
          });
        }
      },
      deleteCheckDonation(donation) {
        this.donationToDelete = donation;
        this.deleteDialog = true;
      },
      deleteDonation() {
        this.deleteButtonLoading = true;
        const id = this.donationToDelete.id;

        this.$apollo
          .mutate({
            mutation: DELETE_SPECIFIC_DONATION_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteSpecificDonation } }) => {
              this.updateDonationsCacheAfterDeleteDonation(
                cache,
                deleteSpecificDonation
              );

              if (this.donationToDelete.assetId) {
                this.updateAssetsCacheAfterDeleteDonation(
                  cache,
                  deleteSpecificDonation
                );
              }
            },
          })
          .then(_response => {
            this.deleteDialog = false;
            this.deleteButtonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateDonationsCacheAfterDeleteDonation(cache, deleteSpecificDonation) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var specificDonations = data.testator.specificDonations;
        var index = specificDonations.findIndex(
          d => d.id == deleteSpecificDonation.id
        );
        specificDonations.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      updateAssetsCacheAfterDeleteDonation(cache, deleteSpecificDonation) {
        try {
          const query = {
            query: TESTATOR_QUERY,
            variables: { id: this.testatorId },
          };
          const data = cache.readQuery(query);

          // Update previous asset to now be unassigned
          const assetPropertyName =
            this.$options.assetPropertyNames[deleteSpecificDonation.assetType];
          var asset = data.testator[assetPropertyName].find(
            a => a.id == deleteSpecificDonation.assetId
          );
          asset.specificDonation = null;

          cache.writeQuery({ ...query, data: data });
        } catch (err) {
          if (err.name == 'Invariant Violation') {
            console.log('TESTATOR_QUERY not in cache store yet.');
          } else {
            throw err;
          }
        }
      },
      displayError() {
        this.showError = true;
        this.buttonLoading = false;
        this.deleteButtonLoading = false;
        this.deleteDialog = false;
      },
      redirectToNext() {
        this.$router.push({
          name: 'OverviewPage',
          hash: '#specific-donations',
        });
      },
      // showReadMore(show) {
      //   this.readMoreInfo = show;
      // },
      drawerOpen() {
        this.readMoreDrawerOpen = true;
      },
      drawerClosed() {
        this.readMoreDrawerOpen = false;
      },
      bequeath(type) {
        if (type === 'asset') {
          this.$router.push({ name: 'AssetDonation' });
        } else if (type === 'money') {
          this.$router.push({ name: 'MoneyDonation' });
        }
      },
    },
    mounted() {
      this.inheritanceChoicesVideo =
        this.whitelabel.videos.inheritanceChoicesVideo;
      this.inheritanceChoicesData = this.whitelabel.inheritanceChoicesData;
    },
  };
</script>

<style scoped lang="scss">
  .bequest-title {
    line-height: 28px;
    padding-bottom: 12px;
  }
  .specific-donation-overview {
    padding-top: 40px;
    padding-bottom: 40px;
    .donations-type-container {
      display: flex;
      flex-direction: column;

      .donations-types-list {
        margin-bottom: 47px;
      }
    }

    .read-more-info {
      border-top: 1px solid #d9d9d9;
      border-bottom: 1px solid #d9d9d9;
      padding: 30px 0;
      h2 {
        font-size: 18px;
        font-weight: 600;
        line-height: 23.4px;
        color: var(--v-primary-base);
      }

      .video-container {
        margin: 26px 0;
        video {
          width: 100%;
          max-width: 100%;
        }
      }

      .read-more {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        width: 123px;
        height: 36px;
        border-radius: 100px;
        border: 1px solid #dcddde;
        color: #282828;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: 0.4px;
        text-align: center;
        cursor: pointer;
        span {
          padding: 8px 16px;
        }
      }
    }

    .read-more-info.bottom-info {
      border-bottom: none;
    }

    .header-row {
      padding-bottom: 40px;
      display: flex;
      gap: 76px;
      h1 {
        flex: 1;
      }
      .specific-donations-description {
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        color: #282828;
        padding-top: 20px;
      }
    }

    .donation-item {
      border-radius: 7px;
      background-color: #ffffff;
      padding: 7px;
      width: 310px;
      max-width: 310px;
      margin: 25px 40px 25px 25px;
      .donation-item-text {
        font-size: 16px;
        padding-left: 5px;

        .asset-name {
          font-weight: bold;
        }
      }
    }
    .button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      margin-top: 30px;
      margin-bottom: 30px;
    }
  }

  @media only screen and (min-width: 0px) and (max-width: 960px) {
    .donations-container {
      align-items: center;
    }
  }
</style>
